import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { BinanceWalletAdminService } from '../../services/binance-wallet-admin.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import {
  BatchResultItem,
  BatchStatusResponse,
  UserWithWallet,
  UsersWithWalletFilters,
  WalletAggregates,
  WalletFilter,
  WalletStatus,
} from '../../interfaces/binance-wallet.interface';

/** Verde de marca, usado en toda la app para dialogos SweetAlert. */
const BRAND = '#1e5a3f';
/** Tope de la lista batch impuesto por el backend. */
const BATCH_MAX = 200;
/** Cadencia del polling de progreso del lote. */
const BATCH_POLL_MS = 2500;

interface KycChecklist {
  frontal: boolean;
  posterior: boolean;
  selfie: boolean;
  datos: boolean;
  completo: boolean;
}

interface Badge {
  label: string;
  cls: string;
}

@Component({
  selector: 'app-binance-link-subaccounts',
  templateUrl: './binance-link-subaccounts.component.html',
  styleUrls: ['./binance-link-subaccounts.component.sass'],
})
export class BinanceLinkSubaccountsComponent implements OnInit, OnDestroy {
  // ── Listado ──────────────────────────────────────────────────────
  users: UserWithWallet[] = [];
  loading = false;

  // ── Filtros ──────────────────────────────────────────────────────
  filterQ = '';
  filterHasWallet: WalletFilter = 'without';
  private search$ = new Subject<string>();
  private searchSub?: Subscription;

  // ── Paginación (1-based, como el resto del dashboard) ────────────
  currentPage = 1;
  pageSize = 20;
  totalItems = 0;
  totalPages = 0;

  // ── Selección múltiple ───────────────────────────────────────────
  selectedIds = new Set<number>();

  // ── KPIs del header ──────────────────────────────────────────────
  aggregates: WalletAggregates | null = null;

  // ── Estado del lote ──────────────────────────────────────────────
  batch: BatchStatusResponse | null = null;
  batchRunning = false;
  private batchTimer?: ReturnType<typeof setTimeout>;
  private destroyed = false;

  readonly BATCH_MAX = BATCH_MAX;

  constructor(
    private readonly walletAdmin: BinanceWalletAdminService,
    private readonly spinner: SpinnerService,
  ) {}

  ngOnInit(): void {
    this.searchSub = this.search$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value) => {
        this.filterQ = value;
        this.currentPage = 1;
        this.loadUsers();
      });
    this.loadDashboard();
    this.loadUsers();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.searchSub?.unsubscribe();
    if (this.batchTimer) clearTimeout(this.batchTimer);
  }

  // ── Carga de datos ───────────────────────────────────────────────

  async loadUsers(): Promise<void> {
    this.loading = true;
    const ref = this.spinner.start();
    try {
      const filters: UsersWithWalletFilters = {
        page: this.currentPage,
        limit: this.pageSize,
        hasWallet: this.filterHasWallet,
      };
      const q = this.filterQ.trim();
      if (q) filters.q = q;

      const res = await this.walletAdmin.listUsersWithWallet(filters);
      this.users = res.data;
      this.totalItems = res.meta.total;
      this.totalPages = res.meta.totalPages;
      this.pruneSelection();
    } catch (e) {
      this.errorToast('No se pudo cargar el listado de usuarios.');
    } finally {
      this.loading = false;
      this.spinner.stop(ref);
    }
  }

  private async loadDashboard(): Promise<void> {
    try {
      const dash = await this.walletAdmin.getDashboard();
      this.aggregates = dash.aggregates;
    } catch {
      // El header es informativo: si falla, no bloquea la vista.
      this.aggregates = null;
    }
  }

  // ── Filtros / paginación ─────────────────────────────────────────

  onSearchChange(value: string): void {
    this.search$.next(value ?? '');
  }

  onFilterHasWalletChange(value: WalletFilter): void {
    this.filterHasWallet = value;
    this.currentPage = 1;
    this.loadUsers();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.loadUsers();
  }

  get rangeFrom(): number {
    return this.totalItems === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }
  get rangeTo(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  // ── Selección ────────────────────────────────────────────────────

  /** Elegible para crear: sin wallet activa. */
  isSelectable(u: UserWithWallet): boolean {
    return u.walletId == null;
  }

  isSelected(u: UserWithWallet): boolean {
    return this.selectedIds.has(u.usuarioId);
  }

  toggleSelect(u: UserWithWallet): void {
    if (!this.isSelectable(u)) return;
    if (this.selectedIds.has(u.usuarioId)) this.selectedIds.delete(u.usuarioId);
    else this.selectedIds.add(u.usuarioId);
  }

  selectablePage(): UserWithWallet[] {
    return this.users.filter((u) => this.isSelectable(u));
  }

  get selectablePageEmpty(): boolean {
    return this.selectablePage().length === 0;
  }

  get allPageSelected(): boolean {
    const page = this.selectablePage();
    return page.length > 0 && page.every((u) => this.selectedIds.has(u.usuarioId));
  }

  toggleSelectAllPage(): void {
    const page = this.selectablePage();
    if (this.allPageSelected) {
      page.forEach((u) => this.selectedIds.delete(u.usuarioId));
    } else {
      page.forEach((u) => this.selectedIds.add(u.usuarioId));
    }
  }

  clearSelection(): void {
    this.selectedIds.clear();
  }

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  /** Quita de la selección ids que ya no están en la página cargada y ya tienen wallet. */
  private pruneSelection(): void {
    const withWalletNow = new Set(
      this.users.filter((u) => u.walletId != null).map((u) => u.usuarioId),
    );
    withWalletNow.forEach((id) => this.selectedIds.delete(id));
  }

  // ── Creación individual ──────────────────────────────────────────

  async crearIndividual(u: UserWithWallet): Promise<void> {
    const kyc = this.kyc(u);
    if (!kyc.completo) {
      const ok = await Swal.fire({
        icon: 'warning',
        title: 'KYC incompleto',
        html: `El usuario <b>${this.userLabel(u)}</b> no tiene el KYC completo. La sub-cuenta quedará inerte hasta enviar el KYC. ¿Crear igual?`,
        showCancelButton: true,
        confirmButtonText: 'Crear igual',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: BRAND,
      });
      if (!ok.isConfirmed) return;
    }

    const ref = this.spinner.start();
    try {
      const wallet = await this.walletAdmin.createWalletForUser(u.usuarioId);
      this.successToast(
        `Sub-cuenta creada para ${this.userLabel(u)} (${wallet.walletStatus}).`,
      );
      this.selectedIds.delete(u.usuarioId);
      await this.loadUsers();
    } catch (e) {
      this.errorToast(this.httpMessage(e, 'No se pudo crear la sub-cuenta.'));
    } finally {
      this.spinner.stop(ref);
    }
  }

  // ── Creación en lote ─────────────────────────────────────────────

  async crearSeleccionadas(): Promise<void> {
    if (this.batchRunning) return;

    const ids = Array.from(this.selectedIds);
    if (ids.length === 0) {
      this.errorToast('No hay usuarios seleccionados.');
      return;
    }
    if (ids.length > BATCH_MAX) {
      this.errorToast(`Máximo ${BATCH_MAX} usuarios por lote. Tienes ${ids.length} seleccionados.`);
      return;
    }

    // Aviso si alguno tiene KYC incompleto (la sub queda inerte).
    const incompletos = this.users.filter(
      (u) => this.selectedIds.has(u.usuarioId) && !this.kyc(u).completo,
    ).length;
    const aviso =
      incompletos > 0
        ? `<br><span style="color:#b45309">${incompletos} con KYC incompleto — esas sub-cuentas quedarán inertes hasta enviar el KYC.</span>`
        : '';

    const confirm = await Swal.fire({
      icon: 'question',
      title: `Crear ${ids.length} sub-cuentas`,
      html: `Se creará una sub-cuenta Binance para <b>${ids.length}</b> usuario(s).${aviso}`,
      showCancelButton: true,
      confirmButtonText: `Crear ${ids.length}`,
      cancelButtonText: 'Cancelar',
      confirmButtonColor: BRAND,
    });
    if (!confirm.isConfirmed) return;

    const ref = this.spinner.start();
    try {
      const { jobId, total } = await this.walletAdmin.createWalletForUserBatch(ids);
      this.batch = {
        jobId,
        status: 'processing',
        total,
        processed: 0,
        ok: 0,
        errors: 0,
        resultados: [],
      };
      this.batchRunning = true;
      this.spinner.stop(ref);
      this.pollBatch(jobId);
    } catch (e) {
      this.spinner.stop(ref);
      if (e instanceof HttpErrorResponse && e.status === 409) {
        this.errorToast('Ya hay un lote en curso, espera a que termine.');
      } else if (e instanceof HttpErrorResponse && e.status === 400) {
        this.errorToast('Lista inválida (vacía, con duplicados o supera el máximo).');
      } else {
        this.errorToast(this.httpMessage(e, 'No se pudo iniciar el lote.'));
      }
    }
  }

  private pollBatch(jobId: string): void {
    if (this.destroyed) return;
    this.batchTimer = setTimeout(async () => {
      try {
        const status = await this.walletAdmin.getBatchStatus(jobId);
        this.batch = status;
        if (status.status === 'processing') {
          this.pollBatch(jobId);
        } else {
          this.finishBatch();
        }
      } catch (e) {
        if (e instanceof HttpErrorResponse && e.status === 404) {
          // El job expiró o cayó en otra instancia. La creación es
          // idempotente: refrescamos la tabla para ver el resultado real.
          this.batchRunning = false;
          this.errorToast(
            'El seguimiento del lote se perdió (posible instancia distinta). Refrescando la tabla para ver el resultado real.',
          );
          await this.loadUsers();
        } else {
          // Error transitorio de red: reintentamos el poll.
          this.pollBatch(jobId);
        }
      }
    }, BATCH_POLL_MS);
  }

  private async finishBatch(): Promise<void> {
    this.batchRunning = false;
    this.clearSelection();
    await this.loadUsers();
    await this.loadDashboard();
  }

  get batchProgressPct(): number {
    if (!this.batch || this.batch.total === 0) return 0;
    return Math.round((this.batch.processed / this.batch.total) * 100);
  }

  /** Ítems del lote que fallaron — permiten reintento. */
  get batchErrors(): BatchResultItem[] {
    return this.batch?.resultados.filter((r) => !r.ok) ?? [];
  }

  /** Reintenta solo los usuarios que fallaron (nuevo lote, idempotente). */
  async reintentarFallidos(): Promise<void> {
    const ids = this.batchErrors.map((r) => r.usuarioId);
    if (ids.length === 0) return;
    this.selectedIds = new Set(ids);
    this.batch = null;
    await this.crearSeleccionadas();
  }

  cerrarPanelLote(): void {
    if (this.batchRunning) return;
    this.batch = null;
  }

  // ── Drill-down / soporte ─────────────────────────────────────────

  async verDetalle(u: UserWithWallet): Promise<void> {
    if (!u.walletId) {
      this.errorToast('El usuario no tiene wallet todavía.');
      return;
    }
    const ref = this.spinner.start();
    try {
      const wallet = await this.walletAdmin.getWalletById(u.walletId);
      const rows: string[] = [
        ['Usuario', this.userLabel(u)],
        ['Sub-cuenta', wallet.subAccountId ?? '—'],
        ['Estado wallet', wallet.walletStatus ?? '—'],
        ['Estado KYC', wallet.kycStatus ?? '—'],
        ['Saldo USDT (últ. sync)', u.lastBalanceUsdt ?? '—'],
        ['TRC20', u.walletTrc20?.address ?? 'no generada'],
      ]
        .map(
          ([k, v]) =>
            `<div style="display:flex;justify-content:space-between;gap:16px;padding:5px 0;border-bottom:1px solid #eee"><span style="color:#667">${k}</span><span style="font-weight:600;font-family:monospace;word-break:break-all">${v}</span></div>`,
        );
      await Swal.fire({
        title: 'Detalle de la wallet',
        html: `<div style="text-align:left;font-size:13px">${rows.join('')}</div>`,
        confirmButtonText: 'Cerrar',
        confirmButtonColor: BRAND,
        width: 520,
      });
    } catch (e) {
      this.errorToast(this.httpMessage(e, 'No se pudo cargar el detalle.'));
    } finally {
      this.spinner.stop(ref);
    }
  }

  async generarTrc20(u: UserWithWallet): Promise<void> {
    if (!u.walletId) return;
    const ref = this.spinner.start();
    try {
      const res = await this.walletAdmin.createTrc20Wallet(u.walletId);
      await Swal.fire({
        icon: 'success',
        title: 'Dirección TRC20',
        html: `<code style="font-size:12px;word-break:break-all">${res.address}</code>`,
        confirmButtonColor: BRAND,
      });
      await this.loadUsers();
    } catch (e) {
      if (e instanceof HttpErrorResponse && e.status === 409) {
        this.errorToast('Pendiente de aprobación de KYC por Binance. Aún no se puede generar la dirección.');
      } else {
        this.errorToast(this.httpMessage(e, 'No se pudo generar la dirección TRC20.'));
      }
    } finally {
      this.spinner.stop(ref);
    }
  }

  async enviarKyc(u: UserWithWallet): Promise<void> {
    const ref = this.spinner.start();
    try {
      const res = await this.walletAdmin.onboardUser(u.usuarioId);
      this.successToast(res.message || 'KYC enviado.');
      await this.loadUsers();
    } catch (e) {
      this.errorToast(this.httpMessage(e, 'No se pudo enviar el KYC.'));
    } finally {
      this.spinner.stop(ref);
    }
  }

  async deshabilitar(u: UserWithWallet): Promise<void> {
    if (!u.walletId) return;
    const confirm = await Swal.fire({
      icon: 'warning',
      title: 'Deshabilitar wallet',
      text: `¿Deshabilitar la wallet de ${this.userLabel(u)}?`,
      showCancelButton: true,
      confirmButtonText: 'Deshabilitar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#b23a2b',
    });
    if (!confirm.isConfirmed) return;
    const ref = this.spinner.start();
    try {
      await this.walletAdmin.disableWallet(u.walletId);
      this.successToast('Wallet deshabilitada.');
      await this.loadUsers();
    } catch (e) {
      this.errorToast(this.httpMessage(e, 'No se pudo deshabilitar.'));
    } finally {
      this.spinner.stop(ref);
    }
  }

  // ── Helpers de presentación ──────────────────────────────────────

  private has(v: unknown): boolean {
    return !!(v && String(v).trim());
  }

  kyc(u: UserWithWallet): KycChecklist {
    const frontal = this.has(u.fotoIdfrontal);
    const posterior = this.has(u.fotoIdPosterior);
    const selfie = this.has(u.selfie);
    const datos =
      this.has(u.fechaNacimiento) &&
      this.has(u.nacionalidad) &&
      this.has(u.direccion) &&
      this.has(u.ciudadResidencia) &&
      this.has(u.sexo);
    return {
      frontal,
      posterior,
      selfie,
      datos,
      completo: frontal && posterior && selfie && datos,
    };
  }

  /** RUT a mostrar: empresa usa rutEmpresa; persona usa numeroId. */
  rut(u: UserWithWallet): string {
    return (u.tipoUsuarioId === 1 ? u.rutEmpresa : u.numeroId) || '—';
  }

  userLabel(u: UserWithWallet): string {
    if (u.tipoUsuarioId === 1 && u.razonSocial) return u.razonSocial;
    return `${u.nombre ?? ''} ${u.apellidos ?? ''}`.trim() || u.email;
  }

  prevalBadge(u: UserWithWallet): Badge {
    const p = u.prevalidation;
    if (!p) return { label: 'Sin prevalidar', cls: 'b-gray' };
    if (p.ocrDocumentExpired) return { label: 'Doc vencido', cls: 'b-amber' };
    if (p.faceMatch && p.ocrOverall === 'OK') return { label: 'Óptimo', cls: 'b-green' };
    return { label: 'Revisar', cls: 'b-red' };
  }

  walletBadge(u: UserWithWallet): Badge {
    const s = u.walletStatus;
    if (!s) return { label: 'Sin wallet', cls: 'b-gray' };
    const map: Record<WalletStatus, string> = {
      ACTIVE: 'b-green',
      KYC_PENDING: 'b-amber',
      CREATING: 'b-blue',
      BLOCKED: 'b-red',
      ERROR: 'b-red',
    };
    return { label: s, cls: map[s] ?? 'b-gray' };
  }

  private successToast(text: string): void {
    Swal.fire({ icon: 'success', title: text, toast: true, position: 'top-end', showConfirmButton: false, timer: 3200, timerProgressBar: true });
  }

  private errorToast(text: string): void {
    Swal.fire({ icon: 'error', title: text, toast: true, position: 'top-end', showConfirmButton: false, timer: 4500, timerProgressBar: true });
  }

  private httpMessage(e: unknown, fallback: string): string {
    if (e instanceof HttpErrorResponse) {
      const body: any = e.error;
      if (body?.message) return Array.isArray(body.message) ? body.message.join(', ') : body.message;
    }
    return fallback;
  }

  /** trackBy para no re-render toda la tabla al refrescar. */
  trackByUser(_i: number, u: UserWithWallet): number {
    return u.usuarioId;
  }
}
